"use client";

// useNettiChat — the ONE client conversation with netti (the ask surface).
// Owns the transcript, the transport to /api/netti/ask (question + a short
// history window + the conversation id so netti holds a thread), the PACED
// REVEAL (the route holds netti's words until they are known to be true; this
// hook then lets them arrive a few at a time, so an answer still reads as
// someone thinking with you), the control frame (navigation, motion, links,
// conversation directives, preferences, MOOD), and the rate-ladder conduct.
//
// The thread lives on the server (netti_conversations) and is mirrored in
// localStorage so the rail paints instantly; `conversationId` is what ties a
// browser to its thread. Past conversations are listed, opened and forgotten
// through /api/netti/conversations — the same record netti's own tools read.
//
// A netti message can carry the places it handed over (`links`, drawn as rows)
// and the few things you might say next (`replies`, drawn above the composer).
// Both come off the control frame through lib/netti/links' cleaners, so nothing
// unsafe is ever drawn. A reply you tap is sent with `send(text, { via: "chip" })`
// and kept on your message as such, which is what netti learns your habits from.

import * as React from "react";
import type { ChatMsg } from "../../components/netti/NettiChat";
import { performExpression } from "./expression";
import { playNettiSound, type NettiSoundName } from "./sound";
import { loadNettiPrefs } from "./prefs";
import { WORKING_MOOD, type Mood } from "./mood";
import { cleanCard, dressFrame, dressMessage, readControl, safetyNetTarget, type ChatFrame } from "./chatFrame";
import { cleanReplies } from "./links";
import type { ChatCard } from "../../components/netti/NettiChat";

const STORE_KEY = "innernet:netti-chat";
const STORE_CAP = 60;
// control characters are the frame grammar — spelled out so no editor ever
// corrupts a literal escape (see the agent's sentinels).
const NUL = String.fromCharCode(0);
const STX = String.fromCharCode(2);
// ETX: everything after it is netti speaking LIVE (a conversation answer the
// route streams as it is written, instead of holding it until the turn ends).
const ETX = String.fromCharCode(3);
/** after a HELD answer's words, once they are final: reveal them now, the frame follows */
const EOT = String.fromCharCode(4);

/**
 * Where this conversation happens. Defaults to the ask surface every signed-in
 * person shares; the operator console passes its own endpoint and its own store
 * so the two threads never bleed into each other. Same netti, same transport,
 * same rate conduct — only the room differs.
 */
export interface NettiChatOpts {
  endpoint?: string;
  storeKey?: string;
  /** the conversations API (list · open · forget). Off for rooms without one. */
  conversationsEndpoint?: string | null;
  /** more fields for each ask's body, read at send time (the deck's read ids). */
  extra?: () => Record<string, unknown>;
}

export interface ConversationRow {
  id: string;
  title: string;
  turn_count: number;
  started_at: string;
  updated_at: string;
  last_path: string | null;
  preview: string;
}

let seq = 0;
const mid = () => `m${Date.now().toString(36)}${(seq++).toString(36)}`;

function load(storeKey: string): ChatMsg[] {
  try {
    // one-time migration: earlier builds kept the thread in sessionStorage (it
    // died with the tab) — carry any such thread into the durable store.
    const raw = localStorage.getItem(storeKey) ?? sessionStorage.getItem(storeKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ChatMsg[];
    return Array.isArray(parsed) ? parsed.slice(-STORE_CAP).map(dressMessage) : [];
  } catch {
    return [];
  }
}

/** The person's presence settings decide whether a mood is performed. */
function perform(mood: Mood | null | undefined, opts: { sound?: NettiSoundName | null } = {}) {
  if (!mood || typeof window === "undefined") return;
  const prefs = loadNettiPrefs();
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (prefs.expressions && !reduce) performExpression(mood.name, { intensity: mood.intensity, sound: false });
  const sound = opts.sound === undefined ? mood.sound : opts.sound;
  if (prefs.sounds && sound) playNettiSound(sound);
}

export function useNettiChat(opts: NettiChatOpts = {}) {
  const endpoint = opts.endpoint ?? "/api/netti/ask";
  const storeKey = opts.storeKey ?? STORE_KEY;
  // the latest extra fields, read at send time without re-binding send
  const extraRef = React.useRef(opts.extra);
  React.useEffect(() => { extraRef.current = opts.extra; });
  const conversationsEndpoint =
    opts.conversationsEndpoint === undefined ? (opts.endpoint ? null : "/api/netti/conversations") : opts.conversationsEndpoint;
  const [msgs, setMsgs] = React.useState<ChatMsg[]>([]);
  const [typing, setTyping] = React.useState(false);
  // the masked activity line — what netti is DOING right now ("reading
  // pricing…"), streamed as STX-framed control text while the words are
  // held. Shown beside the thinking smudges; cleared when the words arrive.
  const [status, setStatus] = React.useState<string | null>(null);
  const [resting, setResting] = React.useState(false);
  const [streamingId, setStreamingId] = React.useState<string | null>(null);
  const [conversationId, setConversationId] = React.useState<string | null>(null);
  const [conversationTitle, setConversationTitle] = React.useState<string>("");
  const busyRef = React.useRef(false);
  const msgsRef = React.useRef<ChatMsg[]>([]);
  msgsRef.current = msgs;
  const convRef = React.useRef<string | null>(null);
  convRef.current = conversationId;
  const revealTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    setMsgs(load(storeKey));
    try {
      const until = Number(localStorage.getItem(`${storeKey}:rest`) ?? sessionStorage.getItem(`${storeKey}:rest`) ?? 0);
      setResting(until > Date.now());
      const conv = localStorage.getItem(`${storeKey}:conv`);
      if (conv) setConversationId(conv);
      const title = localStorage.getItem(`${storeKey}:conv-title`);
      if (title) setConversationTitle(title);
    } catch { /* private mode */ }
    return () => { if (revealTimer.current) clearTimeout(revealTimer.current); };
  }, [storeKey]);

  const persist = React.useCallback((next: ChatMsg[]) => {
    try { localStorage.setItem(storeKey, JSON.stringify(next.slice(-STORE_CAP))); } catch { /* private mode */ }
  }, [storeKey]);

  const adoptConversation = React.useCallback((id: string | null, title = "") => {
    setConversationId(id);
    setConversationTitle(title);
    try {
      if (id) localStorage.setItem(`${storeKey}:conv`, id);
      else localStorage.removeItem(`${storeKey}:conv`);
      if (title) localStorage.setItem(`${storeKey}:conv-title`, title);
      else localStorage.removeItem(`${storeKey}:conv-title`);
    } catch { /* private mode */ }
  }, [storeKey]);

  const push = React.useCallback((who: ChatMsg["who"], text: string, extra?: { via?: "chip" }): string => {
    const id = mid();
    setMsgs((m) => { const next = [...m, { id, who, text, ...(extra?.via ? { via: extra.via } : {}) }].slice(-STORE_CAP); persist(next); return next; });
    return id;
  }, [persist]);

  // a beat of words never writes storage: the thread is saved once a turn
  // settles (and when the page is hidden), not thirty times a second.
  const appendTo = React.useCallback((id: string, delta: string) => {
    setMsgs((m) => m.map((x) => (x.id === id ? { ...x, text: x.text + delta } : x)));
  }, []);
  const saveNow = React.useCallback(() => {
    setMsgs((m) => { const next = m.filter((x) => x.who !== "netti" || x.text.trim()); persist(next); return next; });
  }, [persist]);
  React.useEffect(() => {
    const onHide = () => persist(msgsRef.current.filter((x) => x.who !== "netti" || x.text.trim()));
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [persist]);
  const abortRef = React.useRef<AbortController | null>(null);
  /** stop netti mid-thought: what it already said stays. */
  const stop = React.useCallback(() => { abortRef.current?.abort(); }, []);

  /** hang what the turn handed over on its answer: the rows, and the replies offered next */
  const attach = React.useCallback((id: string, extra: { links?: ChatMsg["links"]; replies?: string[] }) => {
    const links = extra.links?.length ? extra.links : undefined;
    const replies = extra.replies?.length ? extra.replies : undefined;
    if (!links && !replies) return;
    setMsgs((m) => {
      const next = m.map((x) => (x.id === id ? { ...x, ...(links ? { links } : {}), ...(replies ? { replies } : {}) } : x));
      persist(next);
      return next;
    });
  }, [persist]);

  const rest = React.useCallback((seconds = 3600) => {
    setResting(true);
    try { localStorage.setItem(`${storeKey}:rest`, String(Date.now() + seconds * 1000)); } catch { /* private mode */ }
  }, [storeKey]);

  // ── the conversation record ──────────────────────────────────────────────
  /** Start a fresh conversation. The old one stays in the record. */
  const clearChat = React.useCallback((opts: { quiet?: boolean } = {}) => {
    if (revealTimer.current) { clearTimeout(revealTimer.current); revealTimer.current = null; }
    setStreamingId(null);
    setMsgs(() => { persist([]); return []; });
    adoptConversation(null);
    if (!opts.quiet) perform({ name: "rest", intensity: 0.35, sound: "absorb", why: "cleared" });
    window.dispatchEvent(new CustomEvent("innernet:netti-conversations"));
  }, [persist, adoptConversation]);

  const listConversations = React.useCallback(async (query?: string): Promise<ConversationRow[]> => {
    if (!conversationsEndpoint) return [];
    try {
      const r = await fetch(`${conversationsEndpoint}?limit=40${query ? `&q=${encodeURIComponent(query)}` : ""}`, { cache: "no-store" });
      if (!r.ok) return [];
      const j = (await r.json()) as { conversations?: ConversationRow[] };
      return Array.isArray(j.conversations) ? j.conversations : [];
    } catch {
      return [];
    }
  }, [conversationsEndpoint]);

  /** Bring a past conversation back into the rail. */
  const openConversation = React.useCallback(async (id: string): Promise<boolean> => {
    if (!conversationsEndpoint) return false;
    try {
      const r = await fetch(`${conversationsEndpoint}?id=${encodeURIComponent(id)}`, { cache: "no-store" });
      if (!r.ok) return false;
      const j = (await r.json()) as { conversation?: { id: string; title: string; messages: Array<{ id: string; who: ChatMsg["who"]; text: string; links?: ChatMsg["links"]; replies?: string[]; via?: "chip" }>; last_path?: string | null } };
      if (!j.conversation) return false;
      if (revealTimer.current) { clearTimeout(revealTimer.current); revealTimer.current = null; }
      setStreamingId(null);
      const next: ChatMsg[] = j.conversation.messages.map((m) => dressMessage({ id: m.id || mid(), who: m.who, text: m.text, links: m.links, replies: m.replies, via: m.via })).slice(-STORE_CAP);
      setMsgs(() => { persist(next); return next; });
      adoptConversation(j.conversation.id, j.conversation.title);
      perform({ name: "nod", intensity: 0.4, sound: "touch", why: "a conversation came back" });
      return true;
    } catch {
      return false;
    }
  }, [conversationsEndpoint, persist, adoptConversation]);

  const forgetConversation = React.useCallback(async (id: string): Promise<boolean> => {
    if (!conversationsEndpoint) return false;
    try {
      const r = await fetch(conversationsEndpoint, { method: "DELETE", headers: { "content-type": "application/json" }, body: JSON.stringify({ id }) });
      if (!r.ok) return false;
      if (convRef.current === id) clearChat({ quiet: true });
      window.dispatchEvent(new CustomEvent("innernet:netti-conversations"));
      return true;
    } catch {
      return false;
    }
  }, [conversationsEndpoint, clearChat]);

  // ── the reveal ───────────────────────────────────────────────────────────
  // a HELD answer (an order, a lookup that needed checking) arrives whole; we
  // let it land in a couple of seconds so it reads as thought, not a paste. A
  // long answer never drags: the whole reveal is bounded to ~2s.
  const reveal = React.useCallback((id: string, text: string): Promise<void> => {
    return new Promise((resolve) => {
      const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      const hidden = typeof document !== "undefined" && document.hidden;
      const tokens = text.match(/\S+\s*/g) ?? [];
      if (reduce || hidden || tokens.length < 4) { appendTo(id, text); resolve(); return; }
      const per = Math.max(2, Math.ceil(tokens.length / 70));
      const beat = tokens.length > 70 ? 28 : 36;
      let i = 0;
      const tick = () => {
        const n = Math.min(tokens.length - i, i === 0 ? 1 : per);
        appendTo(id, tokens.slice(i, i + n).join(""));
        i += n;
        if (i >= tokens.length) { revealTimer.current = null; resolve(); return; }
        revealTimer.current = setTimeout(tick, beat);
      };
      tick();
    });
  }, [appendTo]);

  // ── netti's own lines ────────────────────────────────────────────────────
  // What netti says without a model: the connect walk in the rail (components/
  // netti/connect). The line reveals the way a held answer does, then its card and
  // its replies land under it. Lines queue behind each other and behind anything
  // netti is still saying, so two voices never write at once. Kept with the thread.
  const speakChain = React.useRef<Promise<unknown>>(Promise.resolve());
  const speak = React.useCallback((text: string, extra: { card?: ChatCard; replies?: string[] } = {}): Promise<string> => {
    const run = async (): Promise<string> => {
      for (let i = 0; busyRef.current && i < 240; i++) await new Promise((r) => setTimeout(r, 250));
      const id = mid();
      const card = cleanCard(extra.card);
      const replies = cleanReplies(extra.replies);
      setMsgs((m) => [...m, { id, who: "netti" as const, text: "", ...(card ? { card } : {}) }].slice(-STORE_CAP));
      setStreamingId(id);
      await reveal(id, text);
      setStreamingId((s) => (s === id ? null : s));
      setMsgs((m) => {
        const next = m.map((x) => (x.id === id ? { ...x, ...(replies.length ? { replies } : {}) } : x));
        persist(next);
        return next;
      });
      return id;
    };
    const p = speakChain.current.then(run, run);
    speakChain.current = p.catch(() => undefined);
    return p;
  }, [reveal, persist]);

  /** a line of the person's, kept as said, that goes to no model (the walk answered it) */
  const post = React.useCallback((text: string, extra?: { via?: "chip" }): string => push("you", text, extra), [push]);

  const send = React.useCallback(
    async (question: string, sendOpts?: { via?: "chip" }) => {
      if (busyRef.current || resting) return;
      busyRef.current = true;
      // a tapped reply is still YOUR words: it is kept as such, marked as a tap
      const via = sendOpts?.via === "chip" ? ("chip" as const) : undefined;
      push("you", question, via ? { via } : undefined);
      setTyping(true);
      let thought = false; // the body has settled into "think" for this turn
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      let answerId: string | null = null;
      let said = ""; // every visible word of this answer, held or live
      try {
        // the conversation so far, oldest first, as {q, a} pairs. The server
        // bounds it; full answers ride along so netti remembers code it wrote.
        const history: Array<{ q?: string; a?: string }> = [];
        for (const m of msgsRef.current.slice(-20)) {
          if (m.who === "you") history.push({ q: m.text });
          else if (m.who === "netti" && history.length) history[history.length - 1].a = m.text;
        }
        const r = await fetch(endpoint, {
          method: "POST",
          headers: { "content-type": "application/json" },
          signal: ctrl.signal,
          // the asker's own clock rides along so netti speaks in THEIR time;
          // the conversation id ties this turn to its thread; the path says
          // where in the dashboard they are standing.
          body: JSON.stringify({
            question,
            history,
            local_time: new Date().toString(),
            conversation_id: convRef.current,
            path: typeof window !== "undefined" ? window.location.pathname : null,
            ...(via ? { via } : {}),
            ...(extraRef.current?.() ?? {}),
          }),
        });

        // rate ladder / gate / auth all return JSON, never a stream
        if (r.status !== 200 || !r.body || !(r.headers.get("content-type") ?? "").includes("text/plain")) {
          const j = (await r.json().catch(() => ({}))) as {
            answer?: string; error?: string; scope?: "breath" | "rest"; mood?: Mood;
          };
          if (r.status === 429) {
            if (j.answer) push("netti", j.answer);
            if (j.scope === "rest") rest(Number(r.headers.get("Retry-After")) || 3600);
            perform(j.mood ?? { name: "rest", intensity: 0.4, sound: null, why: "limited" });
          } else if (r.status === 403) {
            if (j.answer) push("netti", j.answer);
            rest(24 * 3600);
          } else if (r.status === 401) {
            push("notice", "sign in, and i can speak from your memory.");
          } else if (j.answer) {
            push("netti", j.answer);
            perform(j.mood ?? null);
          } else {
            push("notice", j.error ?? "i couldn't reach my voice just now.");
            perform({ name: "shrink", intensity: 0.5, sound: "oops", why: "no voice" });
          }
          setTyping(false);
          return;
        }

        // success: [status frames]* then EITHER held words, OR ETX + live words,
        // then a NUL-marked control frame (JSON, never shown). Frames can split
        // across network chunks; a small state machine peels them out.
        const reader = r.body.getReader();
        const decoder = new TextDecoder();
        let control = "";
        let held = "";
        let live = false;
        let statOpen = false;
        let statAcc = "";
        let pending = "";
        let raf = 0;
        let revealing: Promise<void> | null = null; // a held answer, revealed the moment its words are final
        const ensure = () => answerId ?? (answerId = push("netti", ""));
        const flush = () => {
          raf = 0;
          if (!pending) return;
          const id = ensure();
          appendTo(id, pending);
          pending = "";
        };
        const speak = (t: string) => {
          if (!t) return;
          said += t;
          if (!answerId) {
            ensure();
            setTyping(false);
            setStatus(null);
            setStreamingId(answerId);
          }
          pending += t;
          if (!raf) raf = requestAnimationFrame(flush);
        };
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          let delta = decoder.decode(value, { stream: true });
          if (!delta) continue;
          if (control) { control += delta; continue; }
          while (delta) {
            if (statOpen) {
              const end = delta.indexOf(STX);
              if (end === -1) { statAcc += delta; delta = ""; break; }
              statAcc += delta.slice(0, end);
              if (statAcc.trim() && !live) {
                setStatus(statAcc.trim());
                // the body settles into thought the first time netti reaches
                if (!thought) { thought = true; perform(WORKING_MOOD); }
              }
              statOpen = false; statAcc = "";
              delta = delta.slice(end + 1);
              continue;
            }
            const marks = [delta.indexOf(STX), delta.indexOf(ETX), delta.indexOf(EOT), delta.indexOf(NUL)].filter((x) => x >= 0);
            const at = marks.length ? Math.min(...marks) : -1;
            const text = at === -1 ? delta : delta.slice(0, at);
            if (text) { if (live) speak(text); else held += text; }
            if (at === -1) break;
            const ch = delta[at];
            delta = delta.slice(at + 1);
            if (ch === STX) statOpen = true;
            else if (ch === ETX) { live = true; if (held) { speak(held); held = ""; } }
            else if (ch === EOT) {
              // the held words are final: they land now, while the replies and the record finish
              if (!live && held.trim() && !revealing) {
                said = held;
                const id = ensure();
                setTyping(false);
                setStatus(null);
                setStreamingId(id);
                revealing = reveal(id, held);
                held = "";
              }
            }
            else { control = delta || " "; delta = ""; }
          }
        }
        if (raf) cancelAnimationFrame(raf);
        flush();

        // the frame first — nav, mood, and the conversation directive want to
        // land as the words begin, not after the last one.
        const frame: ChatFrame = readControl(control);
        const { links: placed, replies: offered } = dressFrame(frame);

        if (frame.prefs && (typeof frame.prefs.sounds === "boolean" || typeof frame.prefs.expressions === "boolean")) {
          const { saveNettiPrefs } = await import("./prefs");
          saveNettiPrefs(frame.prefs);
        }
        if (frame.conversation?.id && frame.chat?.action !== "clear") adoptConversation(frame.conversation.id, frame.conversation.title);

        setTyping(false);
        setStatus(null);
        perform(frame.mood ?? null);
        // act on the frame — the performed acts fire FIRST (the shell dissolves
        // the source tiles while they're still on screen), then the nav
        // directive carries the user to the place that changed.
        if (Array.isArray(frame.acts) && frame.acts.length)
          window.dispatchEvent(new CustomEvent("innernet:netti-acts", { detail: { acts: frame.acts } }));
        if (frame.nav) {
          window.dispatchEvent(new CustomEvent("innernet:netti-nav", { detail: { path: frame.nav } }));
        } else if (frame.href) {
          window.dispatchEvent(new CustomEvent("innernet:netti-href", { detail: { href: frame.href } }));
        } else {
          // SAFETY NET — the person plainly asked to be TAKEN somewhere but the
          // model only located instead of calling navigate. Carry them to the
          // top match anyway (the rows still let them pick another).
          const top = safetyNetTarget(question, placed);
          if (top) window.dispatchEvent(new CustomEvent("innernet:netti-nav", { detail: { path: top } }));
        }
        // what netti asks the SCREEN to do: open a connect window, a settings
        // tab, a docs page beside the dashboard (components/netti/NettiUiBridge)
        if (frame.ui && typeof frame.ui === "object" && Object.keys(frame.ui).length)
          window.dispatchEvent(new CustomEvent("innernet:netti-ui", { detail: frame.ui }));

        if (revealing) await revealing;
        if (!live && held.trim()) {
          said = revealing ? said + held : held;
          const id = ensure();
          setStreamingId(id);
          await reveal(id, held);
        }
        if (answerId) attach(answerId, { links: placed, replies: offered });
        setStreamingId(null);
        if (!said.trim()) {
          push("notice", "my words got lost on the way. ask me once more?");
          perform({ name: "shrink", intensity: 0.4, sound: "oops", why: "empty" });
        }
        window.dispatchEvent(new CustomEvent("innernet:memory")); // netti fires as it speaks

        // conversation directives land AFTER the words — the person reads
        // netti's line, then the rail changes under it.
        if (frame.chat?.action === "clear") {
          setTimeout(() => clearChat({ quiet: true }), 900);
        } else if (frame.chat?.action === "open" && frame.chat.id) {
          const id = frame.chat.id;
          setTimeout(() => { void openConversation(id); }, 700);
        } else if (frame.chat?.action === "forget") {
          if (frame.chat.id && frame.chat.id === convRef.current) setTimeout(() => clearChat({ quiet: true }), 900);
          window.dispatchEvent(new CustomEvent("innernet:netti-conversations"));
        }
      } catch (err) {
        const stopped = (err as { name?: string } | null)?.name === "AbortError";
        if (!stopped) {
          push("notice", "i couldn't reach my voice just now.");
          perform({ name: "shrink", intensity: 0.5, sound: "oops", why: "transport" });
        }
      } finally {
        setTyping(false);
        setStatus(null);
        setStreamingId(null);
        abortRef.current = null;
        busyRef.current = false;
        saveNow();
      }
    },
    [push, appendTo, attach, rest, resting, endpoint, reveal, adoptConversation, clearChat, openConversation, saveNow],
  );

  return {
    msgs,
    speak,
    post,
    stop,
    typing,
    resting,
    streamingId,
    status,
    send,
    conversationId,
    conversationTitle,
    clearChat,
    listConversations,
    openConversation,
    forgetConversation,
  };
}
